import { useState, useRef, useEffect, useCallback } from "react";
import { streamShopifySales } from "../services/shopifyService";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import ERPSnakBar from "../components/ERPSnakBar";
import { ColDef } from "ag-grid-community";
import ERPGrid from "../components/ERPGrid";
import { AgGridReact } from "ag-grid-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

function ShopifySale() {
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadedCount, setDownloadedCount] = useState<number>(0);
  const [list, setList] = useState<any[]>([]);
  const [pinnedBottomRows, setPinnedBottomRows] = useState<any[]>([]);
  const GridRef = useRef<AgGridReact>(null);
  const esRef = useRef<EventSource | null>(null);

  const [fromDate, setFromDate] = useState<string>(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    serverity: "",
  });

  useEffect(() => {
    return () => {
      esRef.current?.close();
    };
  }, []);

  const recalculateTotal = useCallback(() => {
    if (!GridRef.current?.api) return;
    let total = 0;
    GridRef.current.api.forEachNodeAfterFilter((node: any) => {
      total += node.data?.sale_qty ?? 0;
    });
    setPinnedBottomRows([{ title: "Total", sale_qty: total }]);
  }, []);

  const columns: ColDef[] = [
    {
      filter: true,
      field: "vendor",
      headerName: "Vendor",
      width: 170,
    },
    {
      field: "product_id",
      headerName: "Product ID",
      width: 170,
    },
    {
      filter: true,
      field: "title",
      headerName: "Title",
      width: 250,
    },
    {
      filter: true,
      field: "barcode",
      headerName: "Barcode",
      width: 160,
    },
    {
      filter: true,
      field: "sku",
      headerName: "SKU",
      width: 150,
    },
    {
      filter: true,
      field: "color",
      headerName: "Color",
      width: 200,
    },
    {
      filter: true,
      field: "size",
      headerName: "Size",
      width: 150,
    },
    {
      filter: true,
      field: "cost_per_item",
      headerName: "Cost per item",
      width: 170,
    },
    {
      filter: true,
      field: "sale_price",
      headerName: "Sale Price",
      width: 160,
    },
    {
      filter: true,
      field: "compare_price",
      headerName: "Compare Price",
      width: 180,
    },
    {
      filter: true,
      field: "sale_qty",
      headerName: "Sale Qty",
      minWidth: 130,
      flex: 1,
    },
  ];

  const handleRefresh = () => {
    esRef.current?.close();
    setLoading(true);
    setDownloadedCount(0);
    setList([]);
    setPinnedBottomRows([]);

    esRef.current = streamShopifySales(
      fromDate,
      toDate,
      (batch, totalSoFar) => {
        // Each batch is a full aggregated snapshot — replace list entirely
        setList(batch);
        setDownloadedCount(totalSoFar);
      },
      (total) => {
        setDownloadedCount(total);
        setLoading(false);
      },
      (message) => {
        setLoading(false);
        setAlert({ open: true, message, serverity: "error" });
      }
    );
  };

  const getRowStyle = (params: any): { backgroundColor: string; fontWeight: string } | undefined => {
    if (params.node.rowPinned) {
      return { backgroundColor: "var(--ag-header-background-color)", fontWeight: "bold" };
    }
    return undefined;
  };

  return (
    <Box component={"div"} sx={{ mt: 0 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ pt: 1, pb: 0.5, pr: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <DatePicker
            label="From Date"
            format="DD-MM-YYYY"
            value={dayjs(fromDate)}
            onChange={(newValue) => {
              if (newValue) setFromDate(dayjs(newValue).format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="To Date"
            format="DD-MM-YYYY"
            value={dayjs(toDate)}
            onChange={(newValue) => {
              if (newValue) setToDate(dayjs(newValue).format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button onClick={handleRefresh} variant="outlined" disabled={loading} size="small">
            Refresh
          </Button>
          {!loading && downloadedCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {downloadedCount.toLocaleString()} records loaded
            </Typography>
          )}
          <Button
            variant="contained"
            size="small"
            disabled={list.length === 0}
            sx={{ ml: "auto" }}
            onClick={() => GridRef.current?.api?.exportDataAsCsv()}
          >
            Export to Excel
          </Button>
        </Box>
      </LocalizationProvider>

      {loading && (
        <Box sx={{ mb: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Downloading sales...
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {downloadedCount.toLocaleString()} rows aggregated
            </Typography>
          </Box>
          <LinearProgress />
        </Box>
      )}

      <Box>
        <ERPGrid
          gridHeight={"80vh"}
          exportToExcel={false}
          ref={GridRef}
          columnDefs={columns}
          rowData={list}
          pinnedBottomRowData={pinnedBottomRows}
          getRowStyle={getRowStyle}
          onFilterChanged={recalculateTotal}
          onFirstDataRendered={recalculateTotal}
          onRowDataUpdated={recalculateTotal}
        />
      </Box>

      <Box>
        <ERPSnakBar
          open={alert.open}
          onClose={() => setAlert({ open: false, message: "", serverity: "" })}
          message={alert.message}
          serverity={alert.serverity}
        />
      </Box>
    </Box>
  );
}

export default ShopifySale;
